import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  BuildingWithTrusses,
  GableTruss,
  HeaderTruss,
  LeanToTruss,
  TrussRequest,
} from '../../../types';
import { showToast } from '../../../utils';
import { postSizeEnumToDisplay } from '../../order/building-constants';
import GableTrussAccordion from '../../order/components/gable-truss-accordion';
import HeaderTrussAccordion from '../../order/components/header-truss-accordion';
import LeanToTrussAccordion from '../../order/components/lean-to-truss-accordion';
import { createEmptyTrussRequest, mapClassificationToFrontend } from '../../order/truss-utils';
import { ProductType } from '../types';

export interface TravelerAddTrussDialogProps {
  open: boolean;
  productType: ProductType | null;
  building: BuildingWithTrusses | null;
  orderDate: string;
  onClose: () => void;
  /** Return true when the cart was saved (closes dialog). False when blocked (e.g. post-size ack on parent). */
  onCommit: (
    nextGable: GableTruss[],
    nextHeader: HeaderTruss[],
    nextLeanTo: LeanToTruss[]
  ) => Promise<boolean>;
}

const TravelerAddTrussDialog = ({
  open,
  productType,
  building,
  orderDate,
  onClose,
  onCommit,
}: TravelerAddTrussDialogProps) => {
  const [gableDraft, setGableDraft] = useState<GableTruss | null>(null);
  const [headerDraft, setHeaderDraft] = useState<HeaderTruss | null>(null);
  const [leanToDraft, setLeanToDraft] = useState<LeanToTruss | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !building || !productType) return;

    const bid = building.id;
    if (productType === 'Gable') {
      const newTruss = createEmptyTrussRequest('Gable', orderDate);
      setGableDraft({
        id: 0,
        buildingId: bid,
        productType: 'Gable',
        classification: newTruss.classification,
        sizeFeet: newTruss.sizeFeet,
        sizeInches: newTruss.sizeInches,
        quantity: newTruss.quantity,
        measurementInsidePost: 0,
        confirmedMeasurementInsidePost: false,
        carriageBolts: false,
        price: 0,
        roofPitch: newTruss.roofPitch || '4:12',
        overhangType: newTruss.overhangType || 'Standard',
      });
      setHeaderDraft(null);
      setLeanToDraft(null);
    } else if (productType === 'Header') {
      const newTruss = createEmptyTrussRequest('Header', orderDate);
      setHeaderDraft({
        id: 0,
        buildingId: bid,
        productType: 'Header',
        classification: newTruss.classification,
        sizeFeet: newTruss.sizeFeet,
        sizeInches: newTruss.sizeInches,
        quantity: newTruss.quantity,
        measurementInsidePost: 0,
        confirmedMeasurementInsidePost: false,
        carriageBolts: false,
        price: 0,
      });
      setGableDraft(null);
      setLeanToDraft(null);
    } else {
      const newTruss = createEmptyTrussRequest('LeanTo', orderDate);
      setLeanToDraft({
        id: 0,
        buildingId: bid,
        productType: 'LeanTo',
        classification: newTruss.classification,
        sizeFeet: newTruss.sizeFeet,
        sizeInches: newTruss.sizeInches,
        quantity: newTruss.quantity,
        measurementInsidePost: 0,
        confirmedMeasurementInsidePost: false,
        carriageBolts: false,
        price: 0,
        roofPitch: newTruss.roofPitch || '5:12',
        overhangType: newTruss.overhangType || 'Standard',
        monoslopeTruss: false,
        connectToHeaderTruss: false,
      });
      setGableDraft(null);
      setHeaderDraft(null);
    }
  }, [open, productType, building, orderDate]);

  const postSizeDisplay = building
    ? postSizeEnumToDisplay[building.postSize] || building.postSize
    : '6x6';

  const handleGableUpdate = useCallback((trussRequest: TrussRequest) => {
    setGableDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        classification: trussRequest.classification,
        sizeFeet: trussRequest.sizeFeet,
        sizeInches: trussRequest.sizeInches,
        quantity: trussRequest.quantity,
        measurementInsidePost: trussRequest.measurementInsidePost,
        confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
        roofPitch: trussRequest.roofPitch || '4:12',
        overhangType: trussRequest.overhangType || 'Standard',
        overhangSize: trussRequest.overhangSize,
        carriageBolts: trussRequest.carriageBolts,
        notes: trussRequest.notes,
        price: trussRequest.price,
      };
    });
  }, []);

  const handleHeaderUpdate = useCallback((trussRequest: TrussRequest) => {
    setHeaderDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        classification: trussRequest.classification,
        sizeFeet: trussRequest.sizeFeet,
        sizeInches: trussRequest.sizeInches,
        quantity: trussRequest.quantity,
        measurementInsidePost: trussRequest.measurementInsidePost,
        confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
        carriageBolts: trussRequest.carriageBolts,
        notes: trussRequest.notes,
        price: trussRequest.price,
      };
    });
  }, []);

  const handleLeanToUpdate = useCallback((trussRequest: TrussRequest) => {
    setLeanToDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        classification: trussRequest.classification,
        sizeFeet: trussRequest.sizeFeet,
        sizeInches: trussRequest.sizeInches,
        quantity: trussRequest.quantity,
        measurementInsidePost: trussRequest.measurementInsidePost,
        confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
        roofPitch: trussRequest.roofPitch || '5:12',
        overhangType: trussRequest.overhangType || 'Standard',
        overhangSize: trussRequest.overhangSize,
        carriageBolts: trussRequest.carriageBolts,
        notes: trussRequest.notes,
        monoslopeTruss: trussRequest.monoslopeTruss || false,
        postSizeOverride: trussRequest.postSizeOverride,
        postSizeOverrideValue: trussRequest.postSizeOverrideValue ?? null,
        connectToHeaderTruss: trussRequest.connectToHeaderTruss || false,
        price: trussRequest.price,
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!building || !productType) return;

    const gable = [...(building.gableTrusses ?? [])].map(t => ({
      ...t,
      classification: mapClassificationToFrontend(t.classification, 'Gable'),
    }));
    const header = [...(building.headerTrusses ?? [])];
    const leanTo: LeanToTruss[] = [...(building.leanToTrusses ?? [])].map(t => ({
      ...t,
      classification: mapClassificationToFrontend(t.classification, 'LeanTo'),
      postSizeOverrideValue: t.postSizeOverrideValue
        ? postSizeEnumToDisplay[t.postSizeOverrideValue] || t.postSizeOverrideValue
        : (t.postSizeOverrideValue ?? null),
    }));

    let nextGable = gable;
    let nextHeader = header;
    let nextLeanTo = leanTo;

    if (productType === 'Gable' && gableDraft) {
      nextGable = [...gable, gableDraft];
    } else if (productType === 'Header' && headerDraft) {
      if (gable.length === 0 && leanTo.length === 0) {
        showToast(
          'Header truss cannot be saved unless a gable or lean-to truss exists on the building.',
          'error'
        );
        return;
      }
      nextHeader = [...header, headerDraft];
    } else if (productType === 'LeanTo' && leanToDraft) {
      nextLeanTo = [...leanTo, leanToDraft];
    } else {
      return;
    }

    const all = [...nextGable, ...nextHeader, ...nextLeanTo];
    const unconfirmed = all.filter(t => !t.confirmedMeasurementInsidePost);
    if (unconfirmed.length > 0) {
      showToast(
        'Please confirm the inside post to post measurement for all trusses before saving.',
        'error'
      );
      return;
    }

    if (nextHeader.length > 0 && nextGable.length === 0 && nextLeanTo.length === 0) {
      showToast(
        'Header truss cannot be saved unless a gable or lean-to truss exists on the building.',
        'error'
      );
      return;
    }

    setSubmitting(true);
    try {
      const ok = await onCommit(nextGable, nextHeader, nextLeanTo);
      if (ok) onClose();
    } finally {
      setSubmitting(false);
    }
  }, [building, productType, gableDraft, headerDraft, leanToDraft, onCommit, onClose]);

  if (!building || !productType) return null;

  const title =
    productType === 'Gable'
      ? 'Add Gable Truss'
      : productType === 'Header'
        ? 'Add Header Truss'
        : 'Add Lean-To Truss';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the new truss, confirm inside post-to-post measurement, then save. The same
          validations apply as on Manage Building / cart update.
        </Typography>
        <Box sx={{ pt: 1, pb: 2 }}>
          {productType === 'Gable' && gableDraft && (
            <GableTrussAccordion
              truss={gableDraft}
              postSize={postSizeDisplay}
              orderDate={orderDate}
              onUpdate={handleGableUpdate}
              onDelete={() => {}}
              defaultExpanded
              hideDeleteButton
            />
          )}
          {productType === 'Header' && headerDraft && (
            <HeaderTrussAccordion
              truss={headerDraft}
              postSize={postSizeDisplay}
              orderDate={orderDate}
              onUpdate={handleHeaderUpdate}
              onDelete={() => {}}
              defaultExpanded
              hideDeleteButton
            />
          )}
          {productType === 'LeanTo' && leanToDraft && (
            <LeanToTrussAccordion
              truss={leanToDraft}
              postSize={postSizeDisplay}
              orderDate={orderDate}
              onUpdate={handleLeanToUpdate}
              onDelete={() => {}}
              defaultExpanded
              hideDeleteButton
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          pt: 3,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={submitting} sx={{ minWidth: 88 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={submitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TravelerAddTrussDialog;
